import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Tree,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Tooltip,
  Skeleton,
  Alert,
  Empty,
  message,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons'
import {
  deleteMenuItem,
  getMenuData,
  getRolesData,
  saveMenuData,
} from '../../../store/menu_editor/action-creators'
import B2BLayout from '../../../layouts/b2b'
import { MapContent, MapTitle, MapWrapper, TaskWrapper } from '../../users/card/styles'
import IconSelect from '../../../components/icon-selector'
import IconComponent from '../../../components/icon-component'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import Result from 'antd/lib/result'

const { TreeNode } = Tree
const { Option } = Select

interface MenuItem {
  id: number
  title?: string
  url?: string
  roles?: number[]
  children?: MenuItem[]
  parent?: number | null
  icon?: string
}

const MenuEditor: React.FC = () => {
  const dispatch = useAppDispatch()
  const { data, isLoading, error, roles, rolesLoading } = useSelector(
    (state: any) => state.menuEditor,
  )
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>('Свойства')
  const [currentItem, setCurrentItem] = useState<MenuItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<MenuItem | null>(null)
  const [deleteError, setDeleteError] = useState('')
  const [form] = Form.useForm()
  const [filteredRoles, setFilteredRoles] = useState(roles)

  useEffect(() => {
    if (roles.length) {
      setFilteredRoles(roles)
    }
  }, [roles])

  useEffect(() => {
    dispatch(getMenuData())
    dispatch(getRolesData())
  }, [dispatch])

  const onAdd = (parentKey: number | null = null) => {
    form.resetFields()
    setSaveError('')
    form.setFieldsValue({
      parent: parentKey,
      title: undefined,
      url: undefined,
      roles: [],
      icon: undefined,
    })
    const item = {
      id: 0,
      parent: parentKey,
      title: undefined,
      url: undefined,
      roles: [],
      icon: undefined,
    }
    setCurrentItem(item)
    setModalTitle(parentKey === null ? 'Новый корневой пункт меню' : 'Новый вложенный пункт меню')
    setModalVisible(true)
  }

  const onEdit = useCallback(
    (item: MenuItem) => {
      setCurrentItem(item)
      form.resetFields()
      setSaveError('')
      form.setFieldsValue({ title: item.title, url: item.url, roles: item.roles, icon: item.icon })
      setModalTitle('Редактирование пункта меню')
      setModalVisible(true)
    },
    [form],
  )

  const handleModalOk = useCallback(async () => {
    if (saving || !currentItem) return
    try {
      const values = await form.validateFields()
      setSaving(true)
      setSaveError('')
      const updateItem = (items: MenuItem[]): MenuItem[] => {
        return items.map(item => {
          if (currentItem?.id === 0 && currentItem?.parent === item?.id) {
            if (item?.children?.length) {
              return { ...item, children: [...item.children, { ...currentItem, ...values }] }
            }
            return { ...item, children: [{ ...currentItem, ...values }] }
          }
          if (item.id === currentItem?.id) {
            return { ...item, ...values }
          }
          if (item.children) {
            return { ...item, children: updateItem(item.children) }
          }
          return item
        })
      }
      const updatedData =
        currentItem.id === 0 && currentItem.parent === null
          ? [...data, { ...currentItem, ...values }]
          : updateItem(data)
      await dispatch(saveMenuData(updatedData))
      setModalVisible(false)
      setCurrentItem(null)
      message.success(currentItem.id === 0 ? 'Пункт меню добавлен' : 'Пункт меню сохранён')
    } catch (error: any) {
      if (!error.errorFields) setSaveError(error.message || 'Не удалось сохранить пункт меню')
    } finally {
      setSaving(false)
    }
  }, [form, currentItem, data, dispatch, saving])

  const handleModalCancel = () => {
    if (saving) return
    setModalVisible(false)
    setCurrentItem(null)
  }

  const countChildren = (item: MenuItem): number =>
    (item.children || []).reduce((count, child) => count + 1 + countChildren(child), 0)

  const handleDelete = async () => {
    if (!deleteTarget || saving) return
    setSaving(true)
    setDeleteError('')
    try {
      await dispatch(deleteMenuItem(deleteTarget.id))
      setDeleteTarget(null)
      message.success('Пункт меню удалён')
    } catch (error: any) {
      setDeleteError(error.message || 'Не удалось удалить пункт меню')
    } finally {
      setSaving(false)
    }
  }

  const renderTreeNodes = (items: MenuItem[]): React.ReactNode => {
    return items.map(item => {
      if (item.children) {
        return (
          <TreeNode title={renderTitle(item)} key={item.id}>
            {renderTreeNodes(item.children)}
          </TreeNode>
        )
      }
      return <TreeNode key={item.id} title={renderTitle(item)} />
    })
  }

  const renderTitle = (item: MenuItem) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {item?.icon && (
        <span style={{ marginRight: 10 }}>
          <IconComponent iconName={item.icon} />
        </span>
      )}
      <span>{item.title}</span>
      <div
        style={{ marginLeft: 15 }}
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
      >
        <Tooltip title="Добавить вложенный пункт">
          <Button
            icon={<PlusOutlined />}
            aria-label={`Добавить вложенный пункт в ${item.title}`}
            disabled={saving}
            size="small"
            type="link"
            onClick={() => onAdd(item.id)}
            style={{ marginRight: 4 }}
          />
        </Tooltip>
        <Tooltip title="Редактировать">
          <Button
            icon={<EditOutlined />}
            aria-label={`Редактировать ${item.title}`}
            disabled={saving}
            size="small"
            type="link"
            onClick={() => onEdit(item)}
            style={{ marginRight: 4 }}
          />
        </Tooltip>
        <Tooltip title="Удалить">
          <Button
            icon={<DeleteOutlined />}
            aria-label={`Удалить ${item.title}`}
            danger
            disabled={saving}
            size="small"
            type="link"
            onClick={() => {
              setDeleteTarget(item)
              setDeleteError('')
            }}
          />
        </Tooltip>
        {!item.roles?.length && (
          <Tooltip
            title="Пункт меню будет скрыт для всех, т.к. не выбраны роли!"
            placement={'right'}
            color={'#F81D22'}
            style={{ maxWidth: 'max-content' }}
          >
            <WarningOutlined style={{ color: '#F81D22', marginLeft: '5px' }} />
          </Tooltip>
        )}
      </div>
    </div>
  )

  const breadcrumbs = [
    {
      url: '/admin',
      title: 'Admin',
    },
  ]

  const onDrop = async (info: any) => {
    if (saving) return
    const dropKey = info.node.key
    const dragKey = info.dragNode.key
    const dropPos = info.node.pos.split('-')
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1])

    const loop = (
      data: MenuItem[],
      id: number,
      callback: (item: MenuItem, index: number, arr: MenuItem[]) => void,
    ) => {
      for (let i = 0; i < data.length; i++) {
        if (+data[i].id === +id) {
          return callback(data[i], i, data)
        }
        if (data[i].children) {
          loop(data[i].children!, +id, callback)
        }
      }
    }

    const cloneItems = (items: MenuItem[]): MenuItem[] =>
      items.map(item => ({
        ...item,
        children: item.children ? cloneItems(item.children) : undefined,
      }))
    const dataCopy = cloneItems(data)

    let dragObj: MenuItem
    loop(dataCopy, +dragKey, (item, index, arr) => {
      arr.splice(index, 1)
      dragObj = item
    })

    if (!info.dropToGap) {
      loop(dataCopy, +dropKey, item => {
        item.children = item.children || []
        item.children.push(dragObj!)
      })
    } else if ((info.node.children || []).length > 0 && info.node.expanded && dropPosition === 1) {
      loop(dataCopy, +dropKey, item => {
        item.children = item.children || []
        item.children.unshift(dragObj!)
      })
    } else {
      let ar: MenuItem[] = []
      let i: number
      loop(dataCopy, +dropKey, (_item, index, arr) => {
        ar = arr
        i = index
      })
      if (dropPosition === -1) {
        ar.splice(i!, 0, dragObj!)
      } else {
        ar.splice(i! + 1, 0, dragObj!)
      }
    }

    setSaving(true)
    try {
      await dispatch(saveMenuData(dataCopy))
    } catch (error: any) {
      message.error(error.message || 'Не удалось изменить порядок пунктов меню')
    } finally {
      setSaving(false)
    }
  }

  const icons = [
    'BarChartOutlined',
    'BankOutlined',
    'StarOutlined',
    'SettingOutlined',
    'HomeOutlined',
    'HeartOutlined',
    'DollarOutlined',
    'UserOutlined',
    'PhoneOutlined',
    'CarOutlined',
    'AreaChartOutlined',
    'PieChartOutlined',
    'LineChartOutlined',
    'FormatPainterOutlined',
    'GlobalOutlined',
    'HistoryOutlined',
    'ProductOutlined',
    'FileExclamationOutlined',
    'ToolOutlined',
    'ColumnWidthOutlined',
    'HammerOutlined',
    'IconCoding',
    'IconPowerBI',
  ]

  const handleSearch = val => {
    const filtered = roles.filter(role => role.name.toLowerCase().includes(val.toLowerCase()))
    setFilteredRoles(filtered)
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {isLoading && <Skeleton active />}
      {!isLoading && error && (
        <Result status={error.statusCode || 'error'} title="Ошибка" subTitle={error.message} />
      )}
      {!isLoading && !error && (
        <>
          <MapWrapper>
            <TaskWrapper style={{ gap: 12, flexWrap: 'wrap' }}>
              <MapTitle>Редактор меню</MapTitle>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={saving}
                onClick={() => onAdd()}
              >
                Добавить корневой пункт
              </Button>
            </TaskWrapper>
            <MapContent>
              {(isLoading || rolesLoading) && <Spin />}
              <p style={{ color: '#6b7280' }}>
                Перетаскивайте пункты, чтобы менять порядок и вложенность. Кнопка «+» рядом с
                пунктом добавляет вложенный пункт.
              </p>
              {!data.length && (
                <Empty description="Пунктов меню пока нет. Добавьте первый корневой пункт." />
              )}
              <Tree draggable={!saving} onDrop={onDrop}>
                {renderTreeNodes(data)}
              </Tree>
              <Modal
                title={modalTitle}
                open={modalVisible}
                confirmLoading={saving}
                okText={currentItem?.id === 0 ? 'Добавить' : 'Сохранить'}
                cancelText="Отмена"
                cancelButtonProps={{ disabled: saving }}
                closable={!saving}
                keyboard={!saving}
                maskClosable={!saving}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
              >
                {saveError && (
                  <Alert type="error" showIcon message={saveError} style={{ marginBottom: 16 }} />
                )}
                <Form form={form} layout="vertical">
                  <Form.Item label="Иконка" name="icon">
                    <IconSelect iconList={icons} onChange={() => {}} />
                  </Form.Item>
                  <Form.Item
                    name="title"
                    label="Название"
                    rules={[{ required: true, message: 'Укажите название!' }]}
                  >
                    <Input autoFocus maxLength={255} />
                  </Form.Item>
                  <Form.Item
                    name="url"
                    label="Ссылка"
                    rules={[{ required: true, message: 'Укажите ссылку!' }]}
                  >
                    <Input
                      maxLength={255}
                      placeholder="Например, /warehouse. Для группы пунктов — #"
                    />
                  </Form.Item>
                  <Form.Item name="roles" label="Роли">
                    <Select
                      mode="multiple"
                      allowClear
                      loading={rolesLoading}
                      placeholder="Выберите роли, которым доступен пункт"
                      onSearch={handleSearch}
                      filterOption={false}
                    >
                      {filteredRoles.map(role => (
                        <Option key={role.id} value={role.id}>
                          {role.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(previous, next) => previous.roles !== next.roles}
                  >
                    {({ getFieldValue }) =>
                      !getFieldValue('roles')?.length && (
                        <Alert
                          type="warning"
                          showIcon
                          message="Выберите роли, чтобы пункт был виден сотрудникам."
                        />
                      )
                    }
                  </Form.Item>
                </Form>
              </Modal>
              <Modal
                title="Удалить пункт меню?"
                open={!!deleteTarget}
                okText="Удалить"
                cancelText="Отмена"
                okButtonProps={{ danger: true }}
                confirmLoading={saving}
                cancelButtonProps={{ disabled: saving }}
                closable={!saving}
                keyboard={!saving}
                maskClosable={!saving}
                onOk={handleDelete}
                onCancel={() => !saving && setDeleteTarget(null)}
              >
                <p>
                  <strong>{deleteTarget?.title}</strong>
                </p>
                <p>
                  {deleteTarget && countChildren(deleteTarget)
                    ? `Будет удалён этот пункт и все вложенные пункты: ${countChildren(
                        deleteTarget,
                      )}.`
                    : 'Пункт будет удалён из меню.'}{' '}
                  Страницы и данные раздела сохранятся.
                </p>
                {deleteError && <Alert type="error" showIcon message={deleteError} />}
              </Modal>
            </MapContent>
          </MapWrapper>
        </>
      )}
    </B2BLayout>
  )
}

export default MenuEditor
